import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';

const PUBLIC_URL = API_URL.replace('/api', '');

const api = axios.create({ withCredentials: true });

function LeadMagnets() {
  const [leadMagnets, setLeadMagnets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [tags, setTags] = useState([]);

  // Formulärdata
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    type: 'pdf',
    description: '',
    fileUrl: '',
    eventDate: '',
    headline: '',
    subheadline: '',
    bulletPoints: ['', '', ''],
    thankYouPageUrl: '',
    autoTags: [],
    deliveryEmailSubject: '',
    isActive: true
  });

  // Hämta lead magnets och taggar vid sidladdning
  useEffect(() => {
    fetchLeadMagnets();
    fetchTags();
  }, []);

  // Hämta lead magnets från API
  const fetchLeadMagnets = async () => {
    try {
      setError('');
      const response = await api.get(`${API_URL}/lead-magnets`);
      setLeadMagnets(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Fel vid hämtning av lead magnets:', error);
      setError('Kunde inte hämta lead magnets. Kontrollera din anslutning.');
      setLoading(false);
    }
  };

  // Hämta taggar
  const fetchTags = async () => {
    try {
      const response = await api.get(`${API_URL}/tags`);
      setTags(response.data.data);
    } catch (error) {
      console.error('Fel vid hämtning av taggar:', error);
    }
  };

  // Generera slug från namn
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/å/g, 'a')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  // Hantera formulärändringar
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'name' && !editingItem) {
      setFormData({
        ...formData,
        name: value,
        slug: generateSlug(value)
      });
    } else if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Hantera bullet points
  const handleBulletChange = (index, value) => {
    const newBullets = [...formData.bulletPoints];
    newBullets[index] = value;
    setFormData({
      ...formData,
      bulletPoints: newBullets
    });
  };

  // Lägg till fler bullet points
  const addBulletPoint = () => {
    setFormData({
      ...formData,
      bulletPoints: [...formData.bulletPoints, '']
    });
  };

  // Ta bort bullet point
  const removeBulletPoint = (index) => {
    if (formData.bulletPoints.length > 1) {
      const newBullets = formData.bulletPoints.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        bulletPoints: newBullets
      });
    }
  };

  // Hantera auto-taggar
  const handleAutoTagChange = (tagName) => {
    const current = formData.autoTags || [];
    if (current.includes(tagName)) {
      setFormData({
        ...formData,
        autoTags: current.filter(t => t !== tagName)
      });
    } else {
      setFormData({
        ...formData,
        autoTags: [...current, tagName]
      });
    }
  };

  // Spara lead magnet
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.slug) {
      setError('Namn och URL-slug krävs');
      return;
    }

    // Filtrera tomma bullet points
    const dataToSend = {
      ...formData,
      bulletPoints: formData.bulletPoints.filter(b => b.trim() !== '')
    };

    try {
      if (editingItem) {
        await api.put(`${API_URL}/lead-magnets/${editingItem.id}`, dataToSend);
        setSuccess('Lead magnet uppdaterad');
      } else {
        await api.post(`${API_URL}/lead-magnets`, dataToSend);
        setSuccess('Lead magnet skapad');
      }

      resetForm();
      fetchLeadMagnets();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Kunde inte spara lead magnet';
      setError(message);
    }
  };

  // Återställ formulär
  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      type: 'pdf',
      description: '',
      fileUrl: '',
      eventDate: '',
      headline: '',
      subheadline: '',
      bulletPoints: ['', '', ''],
      thankYouPageUrl: '',
      autoTags: [],
      deliveryEmailSubject: '',
      isActive: true
    });
    setShowForm(false);
    setEditingItem(null);
  };

  // Redigera lead magnet
  const handleEdit = (item) => {
    setFormData({
      name: item.name || '',
      slug: item.slug || '',
      type: item.type || 'pdf',
      description: item.description || '',
      fileUrl: item.fileUrl || '',
      eventDate: item.eventDate ? item.eventDate.split('T')[0] : '',
      headline: item.headline || '',
      subheadline: item.subheadline || '',
      bulletPoints: item.bulletPoints?.length > 0 ? item.bulletPoints : ['', '', ''],
      thankYouPageUrl: item.thankYouPageUrl || '',
      autoTags: item.autoTags || [],
      deliveryEmailSubject: item.deliveryEmailSubject || '',
      isActive: item.isActive !== false
    });
    setEditingItem(item);
    setShowForm(true);
    setError('');
  };

  // Ta bort lead magnet
  const handleDelete = async (id) => {
    if (window.confirm('Är du säker på att du vill ta bort denna lead magnet?')) {
      try {
        await api.delete(`${API_URL}/lead-magnets/${id}`);
        setSuccess('Lead magnet borttagen');
        fetchLeadMagnets();
        setSelectedItem(null);
        setTimeout(() => setSuccess(''), 3000);
      } catch (error) {
        setError('Kunde inte ta bort lead magnet: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  // Kopiera URL till clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('URL kopierad till urklipp');
    setTimeout(() => setSuccess(''), 2000);
  };

  // Formatera datum
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  // Typ-ikoner
  const getTypeIcon = (type) => {
    const icons = {
      'pdf': '📄',
      'webinar': '🎥',
      'newsletter': '📧',
      'quiz': '❓',
      'checklist': '✅'
    };
    return icons[type] || '📄';
  };

  // Typ-namn
  const getTypeName = (type) => {
    const names = {
      'pdf': 'PDF-guide',
      'webinar': 'Webbinarie',
      'newsletter': 'Nyhetsbrev',
      'quiz': 'Quiz/Test',
      'checklist': 'Checklista'
    };
    return names[type] || type;
  };

  return (
    <main className="App-main">
      {/* Felmeddelanden */}
      <AlertMessage error={error} success={success} />

      {/* Verktygsrad */}
      <div className="toolbar">
        <button
          className="btn btn-primary"
          onClick={() => {
            if (showForm) {
              resetForm();
            } else {
              setShowForm(true);
              setError('');
            }
          }}
        >
          {showForm ? 'Stäng' : '+ Ny Lead Magnet'}
        </button>

        <div className="stats">
          Totalt: {leadMagnets.length} lead magnets |
          Aktiva: {leadMagnets.filter(lm => lm.isActive).length} |
          Totalt nedladdningar: {leadMagnets.reduce((sum, lm) => sum + (lm.downloadCount || 0), 0)}
        </div>
      </div>

      {/* Formulär för ny/redigera lead magnet */}
      {showForm && (
        <div className="form-container lead-magnet-form">
          <h2>{editingItem ? 'Redigera Lead Magnet' : 'Skapa ny Lead Magnet'}</h2>
          <form onSubmit={handleSubmit}>
            {/* Grundinformation */}
            <div className="form-section">
              <h3>Grundinformation</h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Namn *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="T.ex. Projektledarguide 2024"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>URL-slug *</label>
                  <input
                    type="text"
                    name="slug"
                    placeholder="projektledarguide-2024"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Används i URL: /lead-magnet/{formData.slug || 'slug'}</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Typ *</label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                  >
                    <option value="pdf">📄 PDF-guide</option>
                    <option value="webinar">🎥 Webbinarie</option>
                    <option value="newsletter">📧 Nyhetsbrev</option>
                    <option value="quiz">❓ Quiz/Test</option>
                    <option value="checklist">✅ Checklista</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleInputChange}
                    />
                    Aktiv (visa på landningssida)
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Beskrivning</label>
                <textarea
                  name="description"
                  placeholder="Kort beskrivning av lead magneten..."
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="2"
                />
              </div>
            </div>

            {/* Leverans */}
            <div className="form-section">
              <h3>Leverans</h3>

              {(formData.type === 'pdf' || formData.type === 'checklist') && (
                <div className="form-group">
                  <label>Fil-URL (PDF/dokument)</label>
                  <input
                    type="url"
                    name="fileUrl"
                    placeholder="https://example.com/guide.pdf"
                    value={formData.fileUrl}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              {formData.type === 'webinar' && (
                <div className="form-group">
                  <label>Webbinarie-datum</label>
                  <input
                    type="datetime-local"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleInputChange}
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>E-postämne för leveransmejl</label>
                  <input
                    type="text"
                    name="deliveryEmailSubject"
                    placeholder="Här är din guide!"
                    value={formData.deliveryEmailSubject}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label>Omdirigering efter signup (valfritt)</label>
                  <input
                    type="url"
                    name="thankYouPageUrl"
                    placeholder="https://projektledarpodden.se/tack"
                    value={formData.thankYouPageUrl}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Landningssida */}
            <div className="form-section">
              <h3>Landningssida</h3>

              <div className="form-group">
                <label>Rubrik</label>
                <input
                  type="text"
                  name="headline"
                  placeholder="Bli en bättre projektledare!"
                  value={formData.headline}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Underrubrik</label>
                <input
                  type="text"
                  name="subheadline"
                  placeholder="Ladda ner vår kostnadsfria guide med 10 tips..."
                  value={formData.subheadline}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Fördelar/Bullet points</label>
                {formData.bulletPoints.map((bullet, index) => (
                  <div key={index} className="bullet-input">
                    <input
                      type="text"
                      placeholder={`Fördel ${index + 1}...`}
                      value={bullet}
                      onChange={(e) => handleBulletChange(index, e.target.value)}
                    />
                    {formData.bulletPoints.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-danger btn-small"
                        onClick={() => removeBulletPoint(index)}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={addBulletPoint}
                >
                  + Lägg till fördel
                </button>
              </div>
            </div>

            {/* Auto-taggar */}
            <div className="form-section">
              <h3>Auto-taggar</h3>
              <p className="form-help">Välj taggar som automatiskt läggs till på kontakter som signar upp:</p>

              <div className="auto-tags-grid">
                {tags.map(tag => (
                  <label key={tag.id} className="auto-tag-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.autoTags?.includes(tag.name)}
                      onChange={() => handleAutoTagChange(tag.name)}
                    />
                    <span
                      className="tag"
                      style={{ backgroundColor: tag.color, color: '#fff' }}
                    >
                      {tag.name}
                    </span>
                  </label>
                ))}
                {tags.length === 0 && (
                  <p className="no-tags">Inga taggar skapade ännu. Skapa taggar under Kontakter.</p>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingItem ? 'Uppdatera' : 'Skapa'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lead Magnets lista */}
      {loading ? (
        <LoadingSpinner message="Laddar lead magnets..." />
      ) : (
        <div className="lead-magnets-grid">
          {leadMagnets.length === 0 ? (
            <div className="no-items">
              <p>Inga lead magnets skapade ännu</p>
              <p>Klicka på "+ Ny Lead Magnet" för att börja.</p>
            </div>
          ) : (
            leadMagnets.map(item => (
              <div
                key={item.id}
                className={`lead-magnet-card ${!item.isActive ? 'inactive' : ''}`}
                onClick={() => setSelectedItem(selectedItem?.id === item.id ? null : item)}
              >
                <div className="lead-magnet-header">
                  <span className="lead-magnet-type">
                    {getTypeIcon(item.type)} {getTypeName(item.type)}
                  </span>
                  {!item.isActive && <span className="badge inactive">Inaktiv</span>}
                </div>

                <h3>{item.name}</h3>

                {item.description && (
                  <p className="description">{item.description}</p>
                )}

                <div className="lead-magnet-stats">
                  <div className="stat">
                    <span className="stat-value">{item.downloadCount || 0}</span>
                    <span className="stat-label">Signups</span>
                  </div>
                  {item.eventDate && (
                    <div className="stat">
                      <span className="stat-value">{formatDate(item.eventDate)}</span>
                      <span className="stat-label">Event</span>
                    </div>
                  )}
                </div>

                {/* Auto-taggar */}
                {item.autoTags?.length > 0 && (
                  <div className="auto-tags-preview">
                    {item.autoTags.map((tagName, i) => {
                      const tag = tags.find(t => t.name === tagName);
                      return (
                        <span
                          key={i}
                          className="tag small"
                          style={{
                            backgroundColor: tag?.color || '#6B7280',
                            color: '#fff'
                          }}
                        >
                          {tagName}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Expanderad info */}
                {selectedItem?.id === item.id && (
                  <div className="lead-magnet-details" onClick={(e) => e.stopPropagation()}>
                    <div className="detail-section">
                      <h4>Publika URL:er</h4>
                      <div className="url-row">
                        <code>{PUBLIC_URL}/api/public/lead-magnet/{item.slug}</code>
                        <button
                          className="btn btn-small"
                          onClick={() => copyToClipboard(`${PUBLIC_URL}/api/public/lead-magnet/${item.slug}`)}
                        >
                          Kopiera
                        </button>
                      </div>
                      <small>Signup endpoint: POST /api/public/lead-magnet/{item.slug}/signup</small>
                    </div>

                    {item.headline && (
                      <div className="detail-section">
                        <h4>Landningssida</h4>
                        <p><strong>{item.headline}</strong></p>
                        {item.subheadline && <p>{item.subheadline}</p>}
                        {item.bulletPoints?.length > 0 && (
                          <ul>
                            {item.bulletPoints.map((bp, i) => (
                              <li key={i}>{bp}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}

                    {item.fileUrl && (
                      <div className="detail-section">
                        <h4>Fil</h4>
                        <a href={item.fileUrl} target="_blank" rel="noopener noreferrer">
                          {item.fileUrl}
                        </a>
                      </div>
                    )}

                    <div className="lead-magnet-actions">
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => handleEdit(item)}
                      >
                        Redigera
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        Ta bort
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </main>
  );
}

export default LeadMagnets;
